import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { bindTypeaheadReposition, computeFixedTypeaheadStyle } from '../../utils/typeaheadPosition';

export type CffAsyncOption = {
  value: string;
  label: string;
  meta?: unknown;
};

/** Pull code out of typeahead labels like "CODE — Description". */
function codeFromTypedLabel(typed: string): string {
  const text = typed.trim();
  if (!text) return '';
  const spaced = text.match(/^(\S+)\s+[—–\-]\s+(.+)$/u);
  if (spaced?.[1]) return spaced[1].trim();
  const compact = text.match(/^(\S+?)[—–]\s*(.+)$/u);
  if (compact?.[1]) return compact[1].trim();
  return text;
}

type CffAsyncTypeaheadProps = {
  value: string;
  onChange: (value: string) => void;
  onSelect: (option: CffAsyncOption) => void;
  search: (query: string) => Promise<CffAsyncOption[]>;
  placeholder?: string;
  disabled?: boolean;
  inputClassName?: string;
  ariaLabel?: string;
  /** Optional display text when value is set programmatically (e.g. reorder prefill). */
  displayLabel?: string;
  /**
   * On blur, look up an exact code match and call onSelect.
   * If nothing matches, clear the field so half-typed junk is not submitted.
   */
  resolveExactOnBlur?: boolean;
};

export function CffAsyncTypeahead({
  value,
  onChange,
  onSelect,
  search,
  placeholder,
  disabled = false,
  inputClassName = 'field-input',
  ariaLabel,
  displayLabel,
  resolveExactOnBlur = false,
}: CffAsyncTypeaheadProps) {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(() => {
    if (displayLabel?.trim() && value.trim()) return displayLabel.trim();
    return value;
  });
  const [options, setOptions] = useState<CffAsyncOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [listStyle, setListStyle] = useState<React.CSSProperties>({});
  const reqIdRef = useRef(0);
  const editingRef = useRef(false);
  const pickedRef = useRef(false);

  useEffect(() => {
    if (editingRef.current) return;
    if (displayLabel?.trim() && value.trim()) {
      setQuery(displayLabel.trim());
      return;
    }
    setQuery(value);
  }, [value, displayLabel]);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (listRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  const updateListPosition = () => {
    const rect = inputRef.current?.getBoundingClientRect();
    if (!rect) return;
    setListStyle(
      computeFixedTypeaheadStyle(rect, {
        minWidth: Math.max(rect.width, 280),
        maxWidth: 480,
        maxHeightCap: 280,
        gap: 2,
        zIndex: 4200,
      }),
    );
  };

  useEffect(() => {
    if (!open) return;
    updateListPosition();
    return bindTypeaheadReposition(updateListPosition);
  }, [open, query, options.length]);

  useEffect(() => {
    if (!open || disabled) return;
    const q = query.trim();
    if (q.length < 1) {
      setOptions([]);
      setLoading(false);
      return;
    }
    const reqId = ++reqIdRef.current;
    setLoading(true);
    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const rows = await search(q);
          if (reqId !== reqIdRef.current) return;
          setOptions(rows);
          setHighlight(0);
        } catch {
          if (reqId !== reqIdRef.current) return;
          setOptions([]);
        } finally {
          if (reqId === reqIdRef.current) setLoading(false);
        }
      })();
    }, 220);
    return () => window.clearTimeout(timer);
  }, [query, open, disabled, search]);

  const pick = (opt: CffAsyncOption) => {
    editingRef.current = false;
    pickedRef.current = true;
    onSelect(opt);
    // Keep the bound value (e.g. item/customer code) in the input; label stays in the dropdown.
    // Prefer displayLabel when the parent wants a friendlier idle text.
    const idle =
      displayLabel?.trim() && opt.value.trim()
        ? displayLabel.trim()
        : opt.value || opt.label;
    setQuery(idle);
    setOpen(false);
  };

  const syncIdleQuery = () => {
    if (displayLabel?.trim() && value.trim()) {
      setQuery(displayLabel.trim());
    } else {
      setQuery(value);
    }
  };

  const resolveOnBlur = () => {
    editingRef.current = false;
    setOpen(false);

    if (pickedRef.current) {
      pickedRef.current = false;
      syncIdleQuery();
      return;
    }

    if (!resolveExactOnBlur) {
      syncIdleQuery();
      return;
    }

    const typed = query.trim();
    if (!typed) {
      if (value.trim()) onChange('');
      setQuery('');
      return;
    }

    const code = codeFromTypedLabel(typed);
    const reqId = ++reqIdRef.current;
    void (async () => {
      try {
        const rows = await search(code);
        if (reqId !== reqIdRef.current) return;

        const needle = code.toLowerCase();
        const exact =
          rows.find((row) => row.value.trim().toLowerCase() === needle) ??
          (rows.length === 1 && rows[0].value.trim().toLowerCase().startsWith(needle)
            ? rows[0]
            : undefined);

        if (exact) {
          pick(exact);
          return;
        }

        // No reliable match — clear so submit cannot save a half-typed code.
        onChange('');
        setQuery('');
      } catch {
        if (reqId !== reqIdRef.current) return;
        syncIdleQuery();
      }
    })();
  };

  return (
    <div ref={rootRef} className="dem-typeahead cff-async-typeahead">
      <input
        ref={inputRef}
        type="text"
        className={inputClassName}
        value={query}
        placeholder={placeholder}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        role="combobox"
        autoComplete="off"
        onFocus={() => {
          editingRef.current = true;
          pickedRef.current = false;
          // When showing a friendly label, switch to raw code for editing/search.
          if (value.trim() && query !== value) setQuery(value);
          setOpen(true);
          updateListPosition();
        }}
        onBlur={() => {
          resolveOnBlur();
        }}
        onChange={(e) => {
          editingRef.current = true;
          pickedRef.current = false;
          setQuery(e.target.value);
          onChange(e.target.value);
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (!open) {
            if (e.key === 'ArrowDown' || e.key === 'Enter') setOpen(true);
            return;
          }
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlight((prev) => Math.min(prev + 1, Math.max(options.length - 1, 0)));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlight((prev) => Math.max(prev - 1, 0));
          } else if (e.key === 'Enter') {
            e.preventDefault();
            const opt = options[highlight];
            if (opt) pick(opt);
          } else if (e.key === 'Escape') {
            setOpen(false);
          }
        }}
      />
      {open && !disabled
        ? createPortal(
            <ul
              ref={listRef}
              id={listId}
              className="dem-typeahead__list dem-typeahead__list--fixed"
              style={listStyle}
              role="listbox"
            >
              {loading ? <li className="dem-typeahead__empty">Searching…</li> : null}
              {!loading && options.length === 0 ? (
                <li className="dem-typeahead__empty">
                  {query.trim() ? 'No matches' : 'Type to search'}
                </li>
              ) : null}
              {!loading
                ? options.map((opt, index) => (
                    <li key={`${opt.value}-${opt.label}`}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={index === highlight}
                        className={`dem-typeahead__option${
                          index === highlight ? ' dem-typeahead__option--active' : ''
                        }`}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => pick(opt)}
                        onMouseEnter={() => setHighlight(index)}
                      >
                        {opt.label}
                      </button>
                    </li>
                  ))
                : null}
            </ul>,
            document.body,
          )
        : null}
    </div>
  );
}
